import { setTimeoutIfPossible } from "@faire/retailer-visitor-shared/lib/async-functions/setTimeoutIfPossible";
import { circularMod } from "@faire/retailer-visitor-shared/lib/math";
import { useIntersectionObserver } from "@faire/web--source/ui/hooks/useIntersectionObserver";
import { IComponent } from "@faire/web-api--source/indigofair/data/IComponent";
import { useCallback, useEffect, useRef, useState } from "react";

// While we are in migration phase, we don't want to rotate banners
// within this component. It is only used for Conductor so we can
// override for now. When we are ready, we can switch back to
// DEFAULT_COMPONENT_ROTATION_MS = 7500;
const ROTATION_DELAY_MS_FOR_MIGRATION = 999999;

export const useAnimatedRotation = (
  components: IComponent[] | undefined,
  rotationMs: number | undefined
) => {
  const rotationDelayMs = rotationMs ?? ROTATION_DELAY_MS_FOR_MIGRATION;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimationInView, setIsAnimationInView] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const [hasCurrentBannerBeenViewed, setHasCurrentBannerBeenViewed] =
    useState(false);

  const fadeInInterval = useRef<number>(0);
  const fadeOutInterval = useRef<number>(0);

  const bannerViewFlags = useRef<Set<number>>(new Set());

  const containerRef = useIntersectionObserver((e) =>
    setIsInView(e.isIntersecting)
  );

  const pauseScroll = useCallback(() => {
    clearTimeout(fadeOutInterval.current);
    clearTimeout(fadeInInterval.current);
  }, [fadeOutInterval, fadeInInterval]);

  const resumeScroll = useCallback(() => {
    if (!components || components?.length <= 1) {
      return;
    }

    // Make sure there's always only one set of timeouts
    pauseScroll();

    fadeOutInterval.current =
      setTimeoutIfPossible(() => {
        setIsAnimationInView(false);
      }, rotationDelayMs) ?? 0;

    fadeInInterval.current =
      setTimeoutIfPossible(() => {
        setCurrentIndex((currentIndex) =>
          circularMod(currentIndex + 1, components.length)
        );
        setIsAnimationInView(true);
        resumeScroll();
      }, rotationDelayMs + 500) ?? 0;
  }, [components, pauseScroll, rotationDelayMs]);

  useEffect(() => {
    resumeScroll();
    return () => {
      pauseScroll(); // cleaning timeouts
    };
  }, [resumeScroll, pauseScroll]);

  useEffect(() => {
    if (isInView && !bannerViewFlags.current.has(currentIndex)) {
      bannerViewFlags.current.add(currentIndex);
      setHasCurrentBannerBeenViewed(false);
    } else {
      setHasCurrentBannerBeenViewed(true);
    }
  }, [isInView, currentIndex]);

  return {
    containerRef,
    hasCurrentBannerBeenViewed,
    isAnimationInView,
    resumeScroll,
    pauseScroll,
    currentIndex,
  };
};
